import _ from "lodash";
import React from "react";
import classNames from "classnames";
import ACLStore from "lib/aclStore";

import {
  getStyleStrippedText,
  convertHtmlToTextWithSpanConversion,
  getMemoizedLangFunction,
  getThumbnailIcon,
  getRethumbUrl,
  getThumbUrl,
  getAttachmentUrlFromFieldValue,
} from "Utils";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import { isModule, getItemWithDepth, isUnitPlan } from "Utils/EntityFlowUtils";
import HtmlParser from "react-html-parser";
import { getHTMLLabel } from "AppComponents/MultiLevelNodeEditorV2/NodeEditor/Utils/Utils";
import {
  colors,
  PLACEHOLDER_RECT_IMAGE,
  PLACEHOLDER_RECT_IMAGE_COUNT,
} from "Constants";
import RepeatIcon from "./RepeatIcon";
import { MAX_DEPTH } from "./constants";
import { Avatar } from "@toddle-edu/ds-web";
import defaultClasses from "./utils.scss";

export const INDENTATION_WIDTH = 48;
export const CARD_HEIGHT = 56;
export const CARD_PADDING = 12;
export const INDENTATION_WIDTH_COMPACT = 18;

const getDragDepth = offset => _.floor(offset / INDENTATION_WIDTH);

const getSubtreeRelativeDepth = ({ items, activeItem }) => {
  const activeIndex = _.findIndex(items, { id: activeItem.id });
  if (activeIndex === -1) return 0;
  let maxRelativeDepth = 0;
  for (let i = activeIndex + 1; i < items.length; i++) {
    if (items[i].depth <= activeItem.depth) break;
    maxRelativeDepth = Math.max(
      maxRelativeDepth,
      items[i].depth - activeItem.depth
    );
  }
  return maxRelativeDepth;
};

const getMaxDepth = ({
  previousItem,
  nextItem,
  position,
  activeId,
  isUniversalParentEnabled,
}) => {
  if (nextItem?.depth == 1 && position == "top") {
    return 1;
  }

  const isPreviousItemParentEligible = isUniversalParentEnabled
    ? !!previousItem
    : isModule(previousItem);

  if (previousItem && nextItem) {
    if (isPreviousItemParentEligible && position === "bottom") {
      return previousItem.depth + 1;
    } else if (
      previousItem.depth <= nextItem.depth &&
      nextItem.id !== activeId
    ) {
      return nextItem.depth;
    } else {
      return previousItem.depth;
    }
  } else if (previousItem) {
    return isPreviousItemParentEligible
      ? previousItem.depth + 1
      : previousItem.depth;
  }

  return 1;
};

export const getLastItemOfFolder = ({ items, id }) => {
  const itemIndex = _.findIndex(items, { id });
  if (itemIndex === -1) return 1;

  const item = items[itemIndex];

  let i = itemIndex;
  while (i < items.length) {
    i++;

    // corner case: if the item is a collapsed folder at the end of feed
    if (i === items.length) {
      return items[i - 1];
    }

    const nextItem = items[i];

    if (nextItem?.depth <= item.depth) {
      const prevItem = items[i - 1];
      return prevItem;
    }
  }

  return item;
};

const getMinDepthForCollapsedFolder = ({ items, id }) => {
  const itemIndex = _.findIndex(items, { id });
  if (itemIndex === -1) return 1;

  const item = items[itemIndex];

  let i = itemIndex;
  while (i < items.length) {
    i++;
    const nextItem = items[i];

    if (nextItem?.depth <= item.depth) {
      return nextItem.depth;
    }
  }

  return 1;
};

const getMinDepth = ({
  previousItem,
  nextItem,
  items,
  getIsItemExpanded,
  isUniversalParentEnabled,
}) => {
  const isPreviousItemParentEligible = isUniversalParentEnabled
    ? !!previousItem
    : isModule(previousItem);

  if (isPreviousItemParentEligible && !getIsItemExpanded(previousItem.id)) {
    return getMinDepthForCollapsedFolder({ items, id: previousItem.id });
  }

  return nextItem?.depth ?? 1;
};

const getParentId = ({ depth, previousItem, items, overId }) => {
  if (depth == 1 || !previousItem) {
    return null;
  }

  if (depth == previousItem.depth) {
    return previousItem.parent?.id;
  }

  if (depth > previousItem.depth) {
    return previousItem.id;
  }

  const newParent = _.get(
    getItemWithDepth({
      id: overId,
      depth,
      items,
    }),
    "parent.id"
  );

  return newParent ?? null;
};

const getProjectionPosition = ({ activeRect, overRect }) => {
  let position = "top";
  const overHalf = overRect?.top + overRect?.height / 2;

  if (activeRect?.top >= overHalf) {
    position = "bottom";
  }

  return position;
};

// calculate new deltaX for external item
// based on collision rects
const getDeltaX = ({ oldDeltaX, activeRect, overRect, isExternalItem }) => {
  if (!isExternalItem) {
    return oldDeltaX;
  }

  if (!activeRect || !overRect) {
    return oldDeltaX;
  }

  // defining it here instead of top so when
  // external item is removed, this does not get left out
  const DELTA_X_OFFSET = 30;

  const activeRectLeft = activeRect.left;
  const overRectLeft = overRect.left;

  return activeRectLeft - overRectLeft - DELTA_X_OFFSET;
};

const hasLeafChildAsFolder = ({ itemId, childrenConfig, items, leafDepth }) => {
  if (leafDepth <= 0) {
    // Leaf node, check if it's a folder
    const item = _.find(items, { id: itemId });
    return isModule(item);
  }

  const children = childrenConfig[itemId] || [];

  // Recursively check for all children
  return children.some(childId =>
    hasLeafChildAsFolder({
      itemId: childId,
      childrenConfig,
      items,
      leafDepth: leafDepth - 1,
    })
  );
};

export const getProjection = args => {
  const {
    items,
    activeId,
    overId,
    dragMovement,
    getIsItemExpanded,
    resourceRelationConfig,
    customRestriction,
    canHaveUnits,
    maxDepth: _maxDepth = MAX_DEPTH,
    isUniversalParentEnabled = false,
  } = args;

  const { isExternalItem } = splitExternalItemData({ itemId: activeId });

  const overItemIndex = _.findIndex(items, ({ id }) => id == overId);
  const activeItemIndex = _.findIndex(items, ({ id }) => id == activeId);

  const activeItem = !isExternalItem
    ? items[activeItemIndex]
    : { id: activeId, depth: 1 };

  const overItem = items[overItemIndex];

  const isOverItemUnit = isUnitPlan(overItem);

  const isActiveItemUnit = isUnitPlan(activeItem);

  const parentConfig = _.get(resourceRelationConfig, "parentConfig", {});

  let updatedMaxDepth = _maxDepth;

  let overItemAncestorId, isOverItemAncestorUnit;
  if (canHaveUnits && !isActiveItemUnit) {
    // if overItem is unit keep it as a ancestor of it
    overItemAncestorId = isOverItemUnit
      ? overItem.id
      : _.first(parentConfig[overItem?.id]);

    const overItemAncestor = _.find(items, { id: overItemAncestorId });
    isOverItemAncestorUnit = isUnitPlan({
      resourceType: overItemAncestor?.resourceType,
    });

    if (!isOverItemAncestorUnit) updatedMaxDepth--;
  }

  const isActiveItemFolder = isModule(activeItem);

  const { activeRect, delta, overRect } = dragMovement;
  const position = getProjectionPosition({
    activeRect,
    overRect,
  });

  const newIndex = overItemIndex + (position == "top" ? 0 : 1);

  const previousItem = items[newIndex - 1],
    nextItem = items[newIndex];

  const maxDepth = getMaxDepth({
    previousItem,
    nextItem,
    position,
    activeId,
    isUniversalParentEnabled,
  });
  const minDepth = getMinDepth({
    previousItem,
    nextItem,
    items,
    getIsItemExpanded,
    isUniversalParentEnabled,
  });

  const deltaX = getDeltaX({
    activeRect,
    overRect,
    isExternalItem,
    oldDeltaX: _.get(delta, "x", 0),
  });
  const dragDepth = getDragDepth(deltaX);

  let depth = dragDepth + 1;

  if (maxDepth <= minDepth || depth >= maxDepth) {
    depth = maxDepth;
  } else if (depth < minDepth) {
    depth = minDepth;
  }

  let isDisabled = false;

  if (isActiveItemFolder) {
    const childrenMaxDepth =
      _.get(activeItem, "childrenHierarchyInfo.maxDepth") - 1;

    const childrenConfig = _.get(resourceRelationConfig, "childrenConfig", {});

    const extraDepth = hasLeafChildAsFolder({
      itemId: activeItem.id,
      leafDepth: childrenMaxDepth,
      childrenConfig,
      items,
    })
      ? 0
      : 1;

    // if no leaf is a folder then we can add 1 more depth reserved for resources only
    if (depth + childrenMaxDepth >= updatedMaxDepth + extraDepth) {
      isDisabled = true;
    }
  } else {
    const subtreeRelativeDepth = isUniversalParentEnabled
      ? getSubtreeRelativeDepth({ items, activeItem })
      : 0;
    if (depth + subtreeRelativeDepth > updatedMaxDepth) {
      isDisabled = true;
    }
  }

  // will be used to apply custom restrictions for any module
  // pass more required variables if already calculated
  const customRestrictionResponse =
    customRestriction({
      ...args,
      activeItem,
      overItem,
      depth,
      parentConfig,
      overItemAncestorId,
      isOverItemAncestorUnit,
    }) || {};

  return {
    depth,
    maxDepth,
    minDepth,
    updatedMaxDepth,
    position,
    isDisabled,
    previousItemId: previousItem?.id,
    parent: getParentId({ depth, previousItem, items, overId }),
    ...customRestrictionResponse,
  };
};

const getResourceTitleMemoized = getMemoizedLangFunction(({ t, title }) => {
  return _.isEmpty(title) ? t("common:untitled") : title;
});

export const getOrgResourceText = ({ orgResource }) => {
  const { label, resourceType, item, attachment } = orgResource;

  let text = getResourceTitleMemoized({ title: label });

  switch (resourceType) {
    case "ASSESSMENT":
      text = getResourceTitleMemoized({
        title: _.get(item, "title.value", ""),
      });
      break;

    case "ASSIGNMENT":
      text = _.get(item, "content.title.value", "--");
      break;

    case "ORGANIZATION_RESOURCE": {
      const attachmentData = _.get(item, "attachment", {});
      const attachmentDataType = _.get(attachmentData, "type", "");
      const isNote = attachmentDataType === "NOTE";
      text = isNote
        ? _.get(attachmentData, "title", "--")
        : _.get(attachmentData, "name", "--");
      break;
    }
    case "FILE": {
      const attachmentDataType = _.get(attachment, "type", "");
      const isNote = attachmentDataType === "NOTE";
      text = isNote ? _.get(attachment, "title", "--") : label;
      break;
    }
  }

  return text;
};

export const getTextFromOrgResource = ({ orgResource }) => {
  let text = getOrgResourceText({ orgResource });
  const { resourceType } = orgResource;

  const shouldParseHtml =
    resourceType === "ORGANIZATION_RESOURCE" ||
    resourceType === "FILE" ||
    resourceType === "NOTE";

  if (shouldParseHtml) {
    text = convertHtmlToTextWithSpanConversion({
      title: getStyleStrippedText({
        text,
        type: "NOTES_TITLE",
      }),
    });
  }

  return { text, shouldParseHtml };
};

export const getCardLabel = ({ orgResource, highlightText }) => {
  const { text, shouldParseHtml } = getTextFromOrgResource({ orgResource });

  if (_.isEmpty(highlightText))
    return shouldParseHtml ? HtmlParser(text) : text;

  return HtmlParser(
    getHTMLLabel({
      label: text,
      searchText: highlightText,
      style: { color: colors.blue400 },
    })
  );
};

export const getBackgroundColorForImageContainer = ({ type, mimeType }) => {
  switch (type) {
    case "NOTE":
      return colors.surfaceWarning;
    case "LINK":
      return colors.surfaceLink;
    case "AUDIO":
      return colors.decorativeSurfaceTwoL;
    case "VIDEO":
      return colors.surfaceError;
    case "IMAGE":
      return colors.decorativeSurfaceTwoL;
    case "FILE":
      switch (mimeType) {
        case "application/pdf":
          return colors.surfaceError;
        case "application/zip":
          return colors.surfaceWarning;
        case "application/vnd.google-apps.document":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        case "application/msword":
        case "application/x-iwork-pages-sffpages":
        case "application/vnd.apple.pages":
          return colors.surfaceLink;
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        case "application/vnd.google-apps.spreadsheet":
        case "application/vnd.ms-excel":
        case "application/x-iwork-numbers-sffnumbers":
        case "application/vnd.apple.numbers":
        case "text/csv":
          return colors.surfaceSuccess;
        case "application/vnd.google-apps.presentation":
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        case "application/x-iwork-keynote-sffkey":
        case "application/vnd.apple.keynote":
          return colors.surfaceWarning;
        default:
          return colors.surfaceSubtle;
      }
  }
};

const getRethumb = imageUrl => {
  return imageUrl
    ? getRethumbUrl({
        width: 64,
        height: 64,
        imageUrl,
        fitIn: true,
      })
    : "";
};

export const getIcon = getMemoizedLangFunction(
  ({
    orgResource,
    displaySettings,
    classes,
    isCompact,
    source,
    size = "medium",
    unitPlanSize = "small",
    disabled = false,
  }) => {
    const { resourceType, item, attachment, isRepeated = false } = orgResource;
    const showThumbnailImage = _.get(displaySettings, "thumbnail_image", true);

    switch (resourceType) {
      case "UNIT_PLAN": {
        const image = _.get(item, "image", {});

        const imageUrl = getAttachmentUrlFromFieldValue({ fieldValue: image });

        const src =
          !_.isEmpty(imageUrl) && showThumbnailImage
            ? getRethumb(imageUrl)
            : PLACEHOLDER_RECT_IMAGE[item?.id % PLACEHOLDER_RECT_IMAGE_COUNT];

        return (
          <Avatar
            size={unitPlanSize}
            shape="square"
            src={src}
            className={classes.imageContainer || defaultClasses.imageContainer}
            dsVersion="2.0"
            disabled={disabled}
          />
        );
      }
      case "ASSESSMENT":
      case "ASSIGNMENT": {
        const itemData =
          resourceType == "ASSESSMENT" ? item : _.get(item, "content", {});
        const assessmentType = _.get(itemData, "assessmentType.value");
        const imageUrl = _.get(itemData, "image.value", null);

        if (imageUrl && showThumbnailImage) {
          return (
            <div>
              {isRepeated ? (
                <RepeatIcon source={source} isAssessment={true} />
              ) : null}
              <div
                className={
                  classes.imageContainer || defaultClasses.imageContainer
                }
                style={{
                  backgroundImage: `url(${getRethumb(imageUrl)})`,
                  backgroundSize: "cover",
                  backgroundColor: colors["decorativeSurfaceThreeL"],
                }}
              />
            </div>
          );
        } else {
          return (
            <div>
              {isRepeated ? (
                <RepeatIcon source={source} isAssessment={true} />
              ) : null}
              <AssessmentIcon
                assessmentType={assessmentType}
                className={classNames(
                  classes.noShrink || defaultClasses.noShrink,
                  {
                    [classes.compactIcon ||
                    defaultClasses.compactIcon]: isCompact,
                  }
                )}
                size={size}
                disabled={disabled}
              />
            </div>
          );
        }
      }

      case "ORGANIZATION_RESOURCE":
      case "FILE": {
        const attachmentData =
          resourceType == "FILE" ? attachment : _.get(item, "attachment", {});
        const attachmentDataType = attachmentData?.type;
        const attachmentDataMimeType = attachmentData?.mimeType;
        const attachmentDataThumbUrl = attachmentData?.thumbUrl;

        if (attachmentDataType) {
          const hasThumbnaiImage =
            attachmentDataType === "IMAGE" ||
            attachmentDataMimeType === "application/pdf" ||
            (attachmentDataType === "LINK" && attachmentDataThumbUrl);
          const icon = getThumbnailIcon({ ...attachmentData, disabled });

          const { thumbUrl } = hasThumbnaiImage
            ? getThumbUrl({
                attachment: attachmentData,
              })
            : {};

          const containerStyle =
            hasThumbnaiImage && showThumbnailImage
              ? {
                  border: "none",
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                }
              : {
                  border: "none",
                  width: "20px",
                  height: "20px",
                  borderRadius: "8px",
                };

          return (
            <div
              style={{
                backgroundColor: disabled
                  ? colors.surfaceSubtle
                  : getBackgroundColorForImageContainer({
                      type: attachmentDataType,
                      mimeType: attachmentData.mimeType,
                    }),
              }}
              className={classes.iconContainer || defaultClasses.iconContainer}
            >
              {isRepeated ? <RepeatIcon source={source} /> : null}
              <ResourceImagePlaceHolder
                type={attachmentDataType}
                icon={!hasThumbnaiImage || !showThumbnailImage ? icon : null}
                imageUrl={
                  hasThumbnaiImage && showThumbnailImage ? thumbUrl : null
                }
                containerStyle={containerStyle}
              />
            </div>
          );
        }
      }
    }
  },
  ({ orgResource, displaySettings, disabled = false }) => {
    const resourceData = _.pick(orgResource, [
      "resourceType",
      "item",
      "attachment",
      "isRepeated",
    ]);
    const thumbnailImage = _.get(displaySettings, "thumbnail_image", true);
    return `${JSON.stringify(resourceData)}_${thumbnailImage}_${disabled}`;
  }
);

export const splitExternalItemData = ({ itemId }) => {
  const isExternalItem = itemId.search(":") != -1;

  if (!isExternalItem) {
    return { isExternalItem };
  }

  const [type, id] = _.split(itemId, ":");
  return { itemId: id, itemType: type, isExternalItem };
};

/**
 * Line height for the text based on the font-size.
 *
 * @param {number} fontSize - Font size of the text.
 * @returns {string} - Line height corresponding to the provided font-size (default: 16).
 */

const getLineHeight = fontSize => {
  let lineHeight;

  switch (fontSize) {
    case 20:
      lineHeight = 32;
      break;
    case 18:
      lineHeight = 28;
      break;
    case 16:
      lineHeight = 24;
      break;
    case 14:
      lineHeight = 20;
      break;
    default:
      lineHeight = 16;
      break;
  }

  return lineHeight;
};

/**
 * Decreases all inline font-size (in px, including decimals) by a given number of pixels.
 *
 * @param {string} htmlString - The HTML content as a string.
 * @param {number} decrement - Pixels to subtract from each font-size (default: 2).
 * @returns {string} - Modified HTML string with updated font-size values.
 */
export function updateFontStylingInHtml({ htmlString, decrementBy = 2 }) {
  // Match font-size values like "font-size: 16px" or "font-size: 16.5px"
  const fontSizeRegex = /font-size\s*:\s*(\d+(\.\d+)?)px/gi;

  return htmlString.replace(fontSizeRegex, (match, size) => {
    const originalSize = parseFloat(size); // parse decimal or integer
    const newSize = Math.max(1, originalSize - decrementBy);
    const styles = [];

    const fontSizeStyle = `font-size: ${newSize}px`;
    const lineHeightStyle = `line-height: ${getLineHeight(newSize)}px`;
    const fontWeightStyle = "font-weight: 500";

    styles.push(fontSizeStyle, lineHeightStyle, fontWeightStyle);

    return styles.join("; ");
  });
}

/**
 * Highlights whole-word occurrences of a search term inside an HTML string,
 * wrapping them in a tag (default: <mark>) while preserving HTML structure.
 *
 * @param {string} htmlString - The HTML content.
 * @param {string} searchTerm - The word to highlight.
 * @param {string} tagName - The HTML tag to use for highlighting (default: 'mark').
 * @returns {string} - HTML string with highlighted search terms.
 */
export function highlightSearchTermInHtml({
  htmlString = "",
  searchTerm = "",
  tagName = "b",
}) {
  if (!searchTerm) return htmlString;

  // Escape special regex characters in the phrase
  const escapedPhrase = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Match text content between tags that contains the phrase
  const regex = new RegExp(`(>[^<]*?)(${escapedPhrase})([^<]*?<)`, "gi");

  return htmlString.replace(regex, (_, before, match, after) => {
    return `${before}<${tagName} style="color: ${colors.blue400}">${match}</${tagName}>${after}`;
  });
}

export const getIsNewNotesUIEnabled = () => {
  return ACLStore.can("FeatureFlag:NewNotesCardsEnabled");
};
